import React, { useCallback, useEffect, useState } from "react";
import "../../App.css";

import TelegramText from "../../components/kit/Text/TelegramText";
import TelegramScreen from "../../components/kit/Screen/TelegramScreen";
import TelegramHeader from "../../components/kit/Header/TelegramHeader";
import { useTelegram } from "../../hooks/useTelegram";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../components/kit/Loader/Loader";
import { useAlert } from "../../components/providers/AlertProvider";
import { useFullAuth } from "../../hooks/useAuthGuard";
import { useFullStateReloadHandler } from "../../hooks/usePageReloadHandler";
import ErrorHandler from "../../components/kit/ErrorHandler/ErrorHandler";
import { postToAPI, authenticateUser } from "../../db/db";
import { updateSessionToken, updateUserProfile, updateSelectedStore, updateAgent } from "../../components/kit/Redux/Actions";

const InventoryScreen = () => {
  const { webApp, user } = useTelegram();
  const dispatch = useDispatch();
  const { triggerAlert } = useAlert();

  // Auth guard and reload handler
  // const { isAuthenticated } = useFullAuth(); // Disabled - we handle auth manually
  // useFullStateReloadHandler(); // Disabled - causes redirect before our auth completes

  const [isLoading, setIsLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [brands, setBrands] = useState([]);
  const [error, setError] = useState(null);

  const sessionToken = useSelector((state) => state.sessionToken);
  const userProfile = useSelector((state) => state.userProfile);
  const currentSelectedStore = useSelector((state) => state.selectedStore);
  
  const userId = user?.id || "";

  // Fetch inventory data
  const fetchInventory = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!currentSelectedStore?.storeId) {
        setError({
          title: "No Store Selected",
          subtitle: "Please select a store first",
          action: "navigation"
        });
        return;
      }

      const endpoint = `/api/inventory/store/${currentSelectedStore.storeId}`;
      const payload = {};

      const result = await postToAPI(endpoint, payload, sessionToken);

      if (result && result.data) {
        const inventoryData = Array.isArray(result.data) ? result.data : [];
        setInventory(inventoryData);
        setFilteredInventory(inventoryData);

        // Extract unique brands for filtering
        const uniqueBrands = [...new Set(inventoryData.map(item => item.brand))].filter(Boolean);
        setBrands(uniqueBrands.sort());
      } else {
        setInventory([]);
        setFilteredInventory([]);
        setBrands([]);
      }
    } catch (error) {
      console.error("Error fetching inventory:", error);
      setError({
        ...error,
        title: "Inventory Loading Failed",
        subtitle: "Unable to load inventory data",
        action: "fetchInventory"
      });
    } finally {
      setIsLoading(false);
    }
  }, [sessionToken, currentSelectedStore?.storeId]);

  // Filter inventory based on search and brand
  const applyFilters = useCallback(() => {
    let filtered = inventory;

    // Filter by brand
    if (selectedBrand !== "all") {
      filtered = filtered.filter(item => item.brand === selectedBrand);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.name?.toLowerCase().includes(query) ||
        item.brand?.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query)
      );
    }

    setFilteredInventory(filtered);
  }, [inventory, selectedBrand, searchQuery]);

  // Apply filters when search or brand changes
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Initialize authentication and store selection
  useEffect(() => {
    const initializeInventory = async () => {
      setIsInitializing(true);
      
      try {
        // If no session token, authenticate first
        if (!sessionToken && userId) {
          console.log("[InventoryScreen] No session token, authenticating user:", userId);
          const authResult = await authenticateUser(userId);
          console.log("[InventoryScreen] Authentication result:", authResult);
          
          if (authResult?.user?.agent) {
            // Update Redux store with authentication data
            dispatch(updateSessionToken(authResult.user.agent.agentKey));
            dispatch(updateUserProfile({
              userid: userId,
              agentCode: authResult.user.agent.agentCode,
              agentId: authResult.user.agent.agentId,
            }));
            
            // Set the agent for useFullAuth
            dispatch(updateAgent({
              id: authResult.user.agent.agentId,
              agentKey: authResult.user.agent.agentKey,
              agentCode: authResult.user.agent.agentCode,
            }));
            
            // If user has a store assigned, update that too
            if (authResult.user?.store) {
              console.log("[InventoryScreen] Setting store:", authResult.user.store);
              dispatch(updateSelectedStore({
                storeId: authResult.user.store.id,
                storeCode: authResult.user.store.code,
                storeName: authResult.user.store.name,
                storeEmoji: "📦",
              }));
            }
          } else {
            console.log("[InventoryScreen] Authentication failed, no agent found");
            setError({
              title: "Authentication Required",
              subtitle: "Please authenticate through the Store bot first",
              action: "authentication"
            });
            setIsInitializing(false);
            return;
          }
        }
        
        setIsInitializing(false);
      } catch (error) {
        console.error("[InventoryScreen] Initialization error:", error);
        setError({
          title: "Initialization Failed",
          subtitle: "Unable to initialize inventory view",
          action: "initialization"
        });
        setIsInitializing(false);
      }
    };

    initializeInventory();
  }, [userId, dispatch]);

  // Fetch inventory after initialization
  useEffect(() => {
    if (!isInitializing && sessionToken && userProfile && currentSelectedStore) {
      fetchInventory();
    }
  }, [isInitializing, sessionToken, userProfile, currentSelectedStore, fetchInventory]);

  // Telegram WebApp setup
  useEffect(() => {
    webApp.ready();
    webApp.BackButton.hide();
    webApp.MainButton.hide();
  }, [webApp]);

  // Error retry handler
  const handleRetry = useCallback(() => {
    if (error?.action === "fetchInventory") {
      fetchInventory();
    } else {
      fetchInventory();
    }
  }, [error, fetchInventory]);

  // Get stock status indicator
  const getStockStatus = (availableQty) => {
    const qty = parseInt(availableQty) || 0;
    if (qty > 10) return { status: "high", color: "#4CAF50", text: "In Stock" };
    if (qty > 0) return { status: "low", color: "#FF9800", text: "Low Stock" };
    return { status: "out", color: "#F44336", text: "Out of Stock" };
  };

  // Show initialization loader
  if (isInitializing) {
    return <Loader title="Initializing inventory..." />;
  }

  // Don't render if not authenticated (after initialization attempt)
  if (!sessionToken && !isInitializing) {
    return <Loader title="Authenticating..." />;
  }

  // Show error screen if there's an error
  if (error) {
    return (
      <ErrorHandler
        error={error}
        title={error.title || "Inventory Error"}
        subtitle={error.subtitle || "Something went wrong"}
        onRetry={handleRetry}
        showReturnButton={true}
      />
    );
  }

  return (
    <TelegramScreen className="parent-container">
      {!isLoading ? (
        <>
          <TelegramHeader>
            <TelegramText className="telegramTitle">
              📦 INVENTORY DASHBOARD
            </TelegramText>
            <TelegramText className="telegramSubtitle" style={{ textAlign: "center" }}>
              {currentSelectedStore?.storeName || "Store"} - Real-time Stock Levels
            </TelegramText>
          </TelegramHeader>

          {/* Search and Filter Controls */}
          <div style={{ padding: "0 16px", marginBottom: "16px" }}>
            {/* Search Bar */}
            <div style={{ marginBottom: "12px" }}>
              <input
                type="text"
                placeholder="🔍 Search products, brands, categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  fontSize: "16px",
                  boxSizing: "border-box"
                }}
              />
            </div>

            {/* Brand Filter */}
            <div style={{ marginBottom: "12px" }}>
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  backgroundColor: "white"
                }}
              >
                <option value="all">🏷️ All Brands ({inventory.length} items)</option>
                {brands.map(brand => (
                  <option key={brand} value={brand}>
                    {brand} ({inventory.filter(item => item.brand === brand).length})
                  </option>
                ))}
              </select>
            </div>

            {/* Results Count */}
            <TelegramText className="telegramSubtitle" style={{ textAlign: "center", color: "#666" }}>
              Showing {filteredInventory.length} of {inventory.length} items
            </TelegramText>
          </div>

          {/* Inventory List */}
          <div style={{ padding: "0 16px" }}>
            {filteredInventory.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {filteredInventory.map((item, index) => {
                  const stockStatus = getStockStatus(item.availableqty);
                  return (
                    <div
                      key={`${item.id}-${index}`}
                      style={{
                        backgroundColor: "white",
                        border: "1px solid #e0e0e0",
                        borderRadius: "12px",
                        padding: "16px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                      }}
                    >
                      {/* Product Header */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                        <div style={{ flex: 1 }}>
                          <TelegramText className="telegramTitle" style={{ fontSize: "16px", marginBottom: "4px" }}>
                            {item.name || "Unknown Product"}
                          </TelegramText>
                          <TelegramText className="telegramSubtitle" style={{ fontSize: "14px", color: "#666" }}>
                            {item.brand} • {item.category}
                          </TelegramText>
                        </div>
                        <div
                          style={{
                            backgroundColor: stockStatus.color,
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            minWidth: "70px",
                            textAlign: "center"
                          }}
                        >
                          {stockStatus.text}
                        </div>
                      </div>

                      {/* Stock Quantity */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <TelegramText className="telegramSubtitle" style={{ color: "#666" }}>
                          Available Quantity:
                        </TelegramText>
                        <TelegramText 
                          className="telegramTitle" 
                          style={{ 
                            fontSize: "18px", 
                            color: stockStatus.color,
                            fontWeight: "bold"
                          }}
                        >
                          {item.availableqty || 0}
                        </TelegramText>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <TelegramText className="telegramTitle" style={{ marginBottom: "8px" }}>
                  📭 No Items Found
                </TelegramText>
                <TelegramText className="telegramSubtitle">
                  {searchQuery || selectedBrand !== "all" 
                    ? "Try adjusting your search or filter criteria"
                    : "No inventory items available for this store"
                  }
                </TelegramText>
              </div>
            )}
          </div>
        </>
      ) : (
        <Loader title="Loading inventory..." />
      )}
    </TelegramScreen>
  );
};

export default InventoryScreen;